import { useEffect, useState } from 'react';
import { apiJson } from '../api/client.js';
import { FieldErrors } from '../components/FieldErrors.jsx';
import { FormInput } from '../components/FormControls.jsx';
import { ViewAsUserButton } from '../components/ViewAsUserButton.jsx';
import { Modal } from '../components/Modal.jsx';
import { formatDate } from '../utils/formatDate.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

export function AdminTeamPage() {
  const [managers, setManagers] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [role, setRole] = useState('manager');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirmation: '',
  });
  const [editErrors, setEditErrors] = useState(undefined);
  const [savingId, setSavingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const resetCreateForm = () => {
    setName('');
    setEmail('');
    setPassword('');
    setPasswordConfirmation('');
    setSendInvite(true);
    setRole('manager');
    setFieldErrors(undefined);
  };

  const closeCreate = () => {
    setShowCreate(false);
    resetCreateForm();
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiJson('/api/admin/team/managers');
      setManagers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors(undefined);
    setSubmitting(true);
    setNotice('');
    try {
      await apiJson('/api/admin/team/managers', {
        method: 'POST',
        body: sendInvite
          ? { name, email, role, send_invite: true }
          : {
              name,
              email,
              role,
              password,
              password_confirmation: passwordConfirmation,
            },
      });
      closeCreate();
      setNotice(sendInvite ? 'Password setup link sent.' : 'Team member added.');
      await load();
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.body?.errors);
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (manager) => {
    setEditingId(manager.id);
    setEditForm({
      name: manager.name,
      email: manager.email,
      password: '',
      passwordConfirmation: '',
    });
    setEditErrors(undefined);
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditErrors(undefined);
  };

  const saveEdit = async (id) => {
    setSavingId(id);
    setEditErrors(undefined);
    setError('');
    try {
      const body = {
        name: editForm.name,
        email: editForm.email,
      };
      if (editForm.password) {
        body.password = editForm.password;
        body.password_confirmation = editForm.passwordConfirmation;
      }
      await apiJson(`/api/admin/team/managers/${id}`, {
        method: 'PUT',
        body,
      });
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.message);
      setEditErrors(err.body?.errors);
    } finally {
      setSavingId(null);
    }
  };

  const sendPasswordLink = async (manager) => {
    setError('');
    setNotice('');
    try {
      await apiJson(`/api/admin/team/managers/${manager.id}/password-link`, { method: 'POST' });
      setNotice(`Password setup link sent to ${manager.email}.`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this manager?')) {
      return;
    }
    try {
      await apiJson(`/api/admin/team/managers/${id}`, { method: 'DELETE' });
      if (editingId === id) {
        cancelEdit();
      }
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="stack">
      <div
        className="row-actions"
        style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 0 }} data-tour="page-title">
          Team
        </h2>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)} data-tour="team-invite">
          Add team member
        </button>
      </div>
      <p className="muted small">Team admins (managers) and team members with scoped client access.</p>
      <p className="muted">
        Managers can manage clients and projects. They inherit your subscription. Open a manager account with{' '}
        <strong>View as</strong> to see the portal exactly as they do.
      </p>
      {notice ? <p className="muted">{notice}</p> : null}
      {error ? <p className="err">{error}</p> : null}

      <section>
        <h3>Current managers</h3>
        {loading ? <p className="muted">Loading…</p> : null}
        {!loading && managers.length === 0 ? <p className="muted">No managers yet.</p> : null}
        {!loading && managers.length > 0 ? (
          <div className="table-wrap">
            <table className="table table-striped table-hover data-table mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Added</th>
                  <th style={{ minWidth: '18rem' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {managers.map((manager) => {
                  const isEditing = editingId === manager.id;
                  return (
                    <tr key={manager.id}>
                      <td>
                        {isEditing ? (
                          <FormInput
                            value={editForm.name}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                            aria-label={`Name for ${manager.email}`}
                            required
                          />
                        ) : (
                          <>
                            {manager.name}
                            {manager.pending_invite ? <span className="muted small"> · invite pending</span> : null}
                            {manager.is_active === false && !manager.pending_invite ? (
                              <span className="muted small"> · inactive</span>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <FormInput
                            type="email"
                            value={editForm.email}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                            aria-label={`Email for ${manager.name}`}
                            required
                          />
                        ) : (
                          manager.email
                        )}
                      </td>
                      <td>{manager.role === 'team_member' ? 'Team member' : 'Team admin'}</td>
                      <td>{formatDate(manager.created_at)}</td>
                      <td>
                        {isEditing ? (
                          <div className="stack" style={{ gap: '0.5rem', minWidth: '16rem' }}>
                            <FormInput
                              type="password"
                              placeholder="New password (optional)"
                              value={editForm.password}
                              onChange={(e) => setEditForm((prev) => ({ ...prev, password: e.target.value }))}
                              autoComplete="new-password"
                            />
                            <FormInput
                              type="password"
                              placeholder="Confirm new password"
                              value={editForm.passwordConfirmation}
                              onChange={(e) =>
                                setEditForm((prev) => ({ ...prev, passwordConfirmation: e.target.value }))
                              }
                              autoComplete="new-password"
                            />
                            <FieldErrors errors={editErrors} />
                            <div className="d-flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={savingId === manager.id}
                                onClick={() => void saveEdit(manager.id)}
                              >
                                {savingId === manager.id ? 'Saving…' : 'Save'}
                              </button>
                              <button type="button" className="btn btn-secondary btn-sm" onClick={cancelEdit}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex flex-wrap gap-2 align-items-center">
                            {manager.can_impersonate ? (
                              <ViewAsUserButton userId={manager.id} label="View as" />
                            ) : null}
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => void sendPasswordLink(manager)}
                            >
                              Send password link
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => startEdit(manager)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => void remove(manager.id)}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <Modal open={showCreate} title="Add team member" onClose={closeCreate}>
        <form onSubmit={onSubmit} className="stack">
          <label className="field">
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="field">
            <span>Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} />
            <span>Send a link so they can create their own password</span>
          </label>
          {!sendInvite ? (
            <>
              <label className="field">
                <span>Password</span>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </label>
              <label className="field">
                <span>Confirm password</span>
                <input
                  type="password"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  required
                />
              </label>
            </>
          ) : null}
          <label className="field">
            <span>Role</span>
            <SearchableSelect value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="manager">Team admin</option>
              <option value="team_member">Team member</option>
            </SearchableSelect>
          </label>
          <FieldErrors errors={fieldErrors} />
          <div className="row-actions" style={{ marginTop: '0.5rem' }}>
            <button type="button" className="btn-secondary" onClick={closeCreate} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Add team member'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
